import {put, call, all} from 'redux-saga/effects';
import Conflict from "../../entities/conflict";
import * as Actions from "../actions/conflictActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type ConflictAction = {
    conflictId: string;
    conflict: Conflict;
    conflicts: Conflict[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneConflict(action: ConflictAction) {
	const session = tabSession.captureGeneration();
	try {
		const conflict: Conflict = yield call(api.findOne, 'conflict', action.conflictId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.CONFLICT_FETCH_ONE_SUCCESS, conflictId: action.conflictId, conflict, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.CONFLICT_FETCH_ONE_FAILURE, conflictId: action.conflictId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {ConflictAction} action
 */
export function* saveConflict(action: ConflictAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ CONFLICT_SAVE saga', action.conflict?.id);

		if (action.conflict === undefined || action.conflict === null) {
			console.error('saveConflict received a null');
			throw new Error('saveConflict received a null');
		}

		if (!(action.conflict instanceof Conflict)) {
			action.conflict = new Conflict(action.conflict);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.conflict.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.conflict);
			const addResult = yield call(idb.add, 'conflict', action.conflict);
			if (addResult.isErr()) {
				console.error(`⮂ saveConflict: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertConflictSave(action.conflict, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.CONFLICT_SAVE_FAILURE, conflict: action.conflict, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.CONFLICT_SAVE, conflict: action.conflict, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertConflictSave(action.conflict, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.CONFLICT_SAVE_FAILURE, conflict: action.conflict, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {ConflictAction} action
 * @param {Conflict[]} action.conflicts
 */
export function* saveManyConflicts(action: ConflictAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ CONFLICT_SAVE_MANY saga', action.conflicts.map(conflict => conflict.id).join(','));

        if (!Array.isArray(action.conflicts)) {
            console.error('saveManyConflicts expected an array, received', action.conflicts);
            throw new Error('saveManyConflicts expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveConflict), under these queue keys
		const dispatchedAddedOns: number[] = action.conflicts.map(conflict => conflict?.addedOn);
		const revertAll = () => action.conflicts.map((conflict, i) =>
			put(Actions.revertConflictSave(conflict, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: Conflict[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.conflicts.length; i++) {
				const conflict = action.conflicts[i];
				conflict.persisted = false;
				ensureOperationId(conflict);
				toSave.push(conflict);
			}

			const addResult = yield call(idb.add, 'conflict', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyConflicts: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.CONFLICT_SAVE_MANY_FAILURE, conflicts: action.conflicts, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.CONFLICT_SAVE_MANY, conflicts: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.CONFLICT_SAVE_MANY_FAILURE, conflicts: action.conflicts, status: 500, err});

			console.error(`Error saving ${action.conflicts.length} conflicts to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {ConflictAction} action
 */
export function* removeConflict(action: ConflictAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.conflict === undefined || action.conflict === null || !(action.conflict instanceof Conflict)) {
			console.error('removeConflict expected type Conflict, received', action.conflict);
			reportQueued(false);
			throw new Error('removeConflict expected type Conflict');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.conflict.status = 'D';
		action.conflict.addedOn = newHRTimestamp();
		action.conflict.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.conflict);
		const addResult = yield call(idb.add, 'conflict', action.conflict);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeConflict: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.CONFLICT_REMOVE_FAILURE, conflict: action.conflict, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.CONFLICT_REMOVE, conflict: action.conflict});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.CONFLICT_REMOVE_FAILURE, conflict: action.conflict, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this Conflict.
	*
	* @param {ConflictAction} action
	*/
export function* restoreConflict(action: ConflictAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreConflict expected getCurrentUserId() to return a value');
        return;
    }

	if (action.conflict === undefined || action.conflict === null) {
        console.error('restoreConflict expected a Conflict, received null');
        throw new Error('restoreConflict expected a Conflict, received null');
    }
    else if (!(action.conflict instanceof Conflict)) action.conflict = new Conflict(action.conflict); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.conflict.status = 'U';
		action.conflict.addedOn = newHRTimestamp();
		action.conflict.addedBy = userId;
		action.conflict.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.conflict);
		const addResult = yield call(idb.add, 'conflict', action.conflict);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreConflict: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.CONFLICT_RESTORE_FAILURE, conflict: action.conflict, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.CONFLICT_RESTORE, conflict: action.conflict});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.CONFLICT_RESTORE_FAILURE, conflict: action.conflict, status: 500, err});

		console.error('Error restoring from database', err);
	}
}